import { api } from '@/api';
import {
  ProfileUpdate,
  LoginFlag,
  PasswordIn,
  PasswordRecoverIn,
} from '@/interfaces';
import router from '@/router';
import {
  getLocalToken,
  loginErrorToMessage,
  removeLocalToken,
  saveLocalToken,
  ssoErrorToMessage,
} from '@/utils';
import { AxiosError, AxiosResponse } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import {
  commitAddNotification,
  commitRemoveNotification,
  commitSetLoggedIn,
  commitSetLoginError,
  commitSetToken,
  commitSetProfile,
  commitSetPasswordError,
  commitSetProfileError,
  commitSetSsoError,
  commitClearLoginError,
  commitClearSsoError,
} from './mutations';
import { AppNotification, MainState } from './state';

type MainContext = ActionContext<MainState, State>;

export const actions = {
  async actionLogin(
    context: MainContext,
    payload: { username: string; password: string },
  ) {
    try {
      const response = await api.login(payload.username, payload.password);
      if (response.data.result) {
        const token = response.data.result.token.access_token;
        const flag = response.data.result.flag;
        if (flag == LoginFlag.NONE) {
          dispatchLoggedIn(context, { token: token, is_after_password: false });
        } else {
          dispatchRouteSetPasswordIn(context, {
            token: token,
            flag: flag.toString(),
            password: payload.password,
          });
        }
      } else {
        // TODO: remove commitSetLoginError
        commitSetLoginError(context, response.data.error!!);
        commitAddNotification(context, {
          content: loginErrorToMessage(response.data.error!!),
          color: 'error',
        });
        await dispatchLogOut(context);
      }
    } catch (error) {
      commitAddNotification(context, {
        content: 'Error login',
        color: 'error',
      });
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },
  async actionLoginSso(context: MainContext, payload: string) {
    try {
      const response = await api.loginSso(payload);
      if (response.data.result) {
        dispatchLoggedIn(context, {
          token: response.data.result.access_token,
          is_after_password: false,
        });
      } else {
        // TODO: remove commitSetSsoError
        commitSetSsoError(context, response.data.error!!);
        commitAddNotification(context, {
          content: ssoErrorToMessage(response.data.error!!),
          color: 'error',
        });
        await dispatchLogOut(context);
      }
    } catch (error) {
      commitAddNotification(context, {
        content: 'Error SSO login',
        color: 'error',
      });
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },
  async actionLoggedIn(
    context: MainContext,
    payload: { token: string; is_after_password: boolean },
  ) {
    saveLocalToken(payload.token);
    commitSetToken(context, payload.token);
    commitSetLoggedIn(context, true);
    commitClearLoginError(context);
    commitClearSsoError(context);
    await dispatchGetProfile(context);
    await dispatchRouteLoggedIn(context);
    commitAddNotification(context, {
      content: payload.is_after_password
        ? 'Password successfully set'
        : 'Logged in',
      color: 'success',
    });
  },
  async actionGetProfile(context: MainContext) {
    try {
      const response = await api.getMe(context.state.token);
      if (response.data) {
        commitSetProfile(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },
  async actionUpdateProfile(context: MainContext, payload: ProfileUpdate) {
    const notification = { content: 'Saving profile', showProgress: true };
    try {
      commitAddNotification(context, notification);
      const response = await api.updateMe(context.state.token, payload);
      commitRemoveNotification(context, notification);
      if (response.data.result) {
        commitSetProfile(context, response.data.result);
        commitAddNotification(context, {
          content: 'Profile successfully updated',
          color: 'success',
        });
      } else if (response.data.error) {
        commitSetProfileError(context, response.data.error);
      } else if (response.data.password_error) {
        commitSetPasswordError(context, response.data.password_error);
      }
    } catch (error) {
      commitRemoveNotification(context, notification);
      commitAddNotification(context, {
        content: 'Error updating profile',
        color: 'error',
      });
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },
  async actionCheckLoggedIn(context: MainContext) {
    if (!context.state.isLoggedIn) {
      let token = context.state.token;
      if (!token) {
        const localToken = getLocalToken();
        if (localToken) {
          commitSetToken(context, localToken);
          token = localToken;
        }
      }
      if (token) {
        try {
          const response = await api.getMe(token);
          commitSetLoggedIn(context, true);
          commitSetProfile(context, response.data);
        } catch (error) {
          await dispatchRemoveLogIn(context);
        }
      } else {
        await dispatchRemoveLogIn(context);
      }
    }
  },
  async actionRemoveLogIn(context: MainContext) {
    removeLocalToken();
    commitSetToken(context, '');
    commitSetLoggedIn(context, false);
  },
  async actionLogOut(context: MainContext) {
    await dispatchRemoveLogIn(context);
    await dispatchRouteLogOut(context);
  },
  async actionUserLogOut(context: MainContext) {
    await dispatchLogOut(context);
    commitAddNotification(context, { content: 'Logged out', color: 'success' });
  },
  actionRouteLogOut(context: MainContext) {
    if (router.currentRoute.path !== '/login') {
      router.push('/login');
    }
  },
  async actionCheckExpiredSession(
    context: MainContext,
    response: AxiosResponse,
  ) {
    if (response!.status === 401) {
      await dispatchLogOut(context);
    }
  },
  async actionCheckApiError(context: MainContext, payload: AxiosError) {
    if (payload.response!.status === 401) {
      await dispatchLogOut(context);
    }
  },
  actionRouteSetPasswordIn(
    context: MainContext,
    payload: { token: string; flag: string; password: string },
  ) {
    if (router.currentRoute.path === '/login') {
      router.push({
        name: 'password/set',
        params: payload,
      });
    }
  },
  actionRouteLoggedIn(context: MainContext) {
    if (
      router.currentRoute.path === '/login' ||
      router.currentRoute.path === '/redirect' ||
      router.currentRoute.path === '/set-password' ||
      router.currentRoute.path === '/'
    ) {
      router.push('/main');
    }
  },
  async removeNotification(
    context: MainContext,
    payload: { notification: AppNotification; timeout: number },
  ) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async setPassword(
    context: MainContext,
    payload: { token: string; data: PasswordIn },
  ) {
    const loadingNotification = {
      content: 'Setting password',
      showProgress: true,
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = await api.setPassword(payload.token, payload.data);
      if (response.data.result) {
        dispatchLoggedIn(context, {
          token: payload.token,
          is_after_password: true,
        });
      } else if (response.data.error) {
        commitSetLoginError(context, response.data.error);
      } else if (response.data.password_error) {
        commitSetPasswordError(context, response.data.password_error);
      }
      commitRemoveNotification(context, loadingNotification);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        color: 'error',
        content: 'Error setting password',
      });
    }
  },
  async forgotPassword(context: MainContext, payload: PasswordRecoverIn) {
    const loadingNotification = {
      content: 'Sending password recovery email',
      showProgress: true,
    };
    try {
      commitAddNotification(context, loadingNotification);
      await api.forgotPassword(context.rootState.main.token, payload);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: 'Password recovery email sent',
        color: 'success',
      });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: 'Error sending recovery email',
        color: 'error',
      });
      await dispatchCheckApiError(context, error as AxiosError);
    }
  },
  async resetPassword(
    context: MainContext,
    payload: { password: string; token: string },
  ) {
    const loadingNotification = {
      content: 'Resetting password',
      showProgress: true,
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.resetPassword(payload.password, payload.token),
          await new Promise<void>((resolve, reject) =>
            setTimeout(() => resolve(), 500),
          ),
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: 'Password successfully reset',
        color: 'success',
      });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        color: 'error',
        content: 'Error resetting password',
      });
    }
  },
};

const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckExpiredSession = dispatch(
  actions.actionCheckExpiredSession,
);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchGetProfile = dispatch(actions.actionGetProfile);
export const dispatchLogin = dispatch(actions.actionLogin);
export const dispatchLoginSso = dispatch(actions.actionLoginSso);
export const dispatchLoggedIn = dispatch(actions.actionLoggedIn);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteSetPasswordIn = dispatch(
  actions.actionRouteSetPasswordIn,
);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateProfile = dispatch(actions.actionUpdateProfile);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchSetPassword = dispatch(actions.setPassword);
export const dispatchForgotPassword = dispatch(actions.forgotPassword);
export const dispatchResetPassword = dispatch(actions.resetPassword);
